/**
 * Centralized HTTP Client for IT Maturity Assessment Application
 *
 * This is the SINGLE source of truth for all HTTP communication with the backend.
 * All feature API modules import this client — no other file should create axios instances.
 *
 * Responsibilities:
 * - Axios instance creation with base URL
 * - Request interceptor: attach JWT from persisted auth store
 * - Response interceptor: 401 → token refresh → retry, or redirect to login
 */

import axios, { InternalAxiosRequestConfig, AxiosError } from 'axios'
import { isProblemDetails } from '@/shared/types'
import type { ProblemDetails } from '@/shared/types'

// In development, use '' (empty) so requests go through Vite's dev proxy.
// In production, VITE_API_URL should be set, or nginx handles routing.
const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export const httpClient = axios.create({
    baseURL: `${API_BASE_URL}/api`,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
})

// ---------------------------------------------------------------------------
// Request Interceptor — Attach JWT token
// ---------------------------------------------------------------------------
httpClient.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const stored = localStorage.getItem('auth-storage')
        if (stored) {
            try {
                const authData = JSON.parse(stored)
                const token = authData.state?.accessToken
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
            } catch {
                // Corrupted storage — ignore silently
            }
        }
        return config
    },
    (error: AxiosError) => Promise.reject(error),
)

// ---------------------------------------------------------------------------
// Response Interceptor — Parse ProblemDetails from error responses
// ---------------------------------------------------------------------------
httpClient.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        const contentType = error.response?.headers?.['content-type'] || ''
        if (
            error.response &&
            contentType.includes('application/problem+json') &&
            isProblemDetails(error.response.data)
        ) {
            // Attach parsed ProblemDetails to the error for downstream consumers
            ; (error as any).problemDetails = error.response.data as ProblemDetails
        }
        return Promise.reject(error)
    },
)

// ---------------------------------------------------------------------------
// Response Interceptor — Handle 401 with token refresh
// ---------------------------------------------------------------------------
httpClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config

        // Skip redirect for auth endpoints to avoid redirect loops
        const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
            originalRequest?.url?.includes('/auth/refresh')

        if (
            error.response?.status === 401 &&
            originalRequest &&
            !(originalRequest as any)._retry &&
            !isAuthEndpoint
        ) {
            (originalRequest as any)._retry = true

            try {
                const stored = localStorage.getItem('auth-storage')
                if (stored) {
                    const authData = JSON.parse(stored)
                    const refreshToken = authData.state?.refreshToken

                    if (refreshToken) {
                        const response = await axios.post(
                            `${API_BASE_URL}/api/auth/refresh`,
                            {},
                            { headers: { Authorization: `Bearer ${refreshToken}` } },
                        )

                        const { access_token } = response.data

                        // Persist the new token
                        authData.state.accessToken = access_token
                        localStorage.setItem('auth-storage', JSON.stringify(authData))

                        // Retry original request
                        originalRequest.headers.Authorization = `Bearer ${access_token}`
                        return httpClient(originalRequest)
                    }
                }
            } catch {
                // Refresh failed — fall through to force logout below
            }

            // No stored auth, no refresh token, or refresh failed — force logout
            localStorage.removeItem('auth-storage')
            window.location.href = '/auth/login'
            return Promise.reject(error)
        }

        return Promise.reject(error)
    },
)
